import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "../../config/api";
import {
  canManageGroup,
  formatGroupDeleteAt,
  isGroupDeactivated,
  isGroupPendingDelete,
} from "../../utils/groupRole";
import { isAdmin as isSiteAdmin } from "../../utils/userRole";

const API_URL = getApiBaseUrl();

const COVER_FALLBACK =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="240" viewBox="0 0 800 240">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#e0f2fe"/>
          <stop offset="55%" stop-color="#f1f5f9"/>
          <stop offset="100%" stop-color="#ccfbf1"/>
        </linearGradient>
      </defs>
      <rect width="800" height="240" fill="url(#g)"/>
      <text x="50%" y="52%" text-anchor="middle" fill="#94a3b8" font-family="Segoe UI, Arial, sans-serif" font-size="22">Group Cover</text>
    </svg>`
  );

export default function GroupInfo({ groupId, members, user, guestView = false, onGroupUpdated }) {
  const [groupInfo, setGroupInfo] = useState({
    name: "",
    description: "",
    photo: "",
    membersCount: 0,
    type: "",
    createdBy: null,
    is_active: 1,
    scheduled_delete_at: null,
    pending_delete: false,
    is_deactivated: false,
  });

  const [form, setForm] = useState({
    name: "",
    description: "",
    photo: null,
  });

  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const navigate = useNavigate();
  const numericGroupId = Number(groupId);
  const isAdmin = isSiteAdmin(user);
  const canManage = canManageGroup(user, members, groupInfo.createdBy);
  const pendingDelete = isGroupPendingDelete(groupInfo);
  const deactivated = isGroupDeactivated(groupInfo);

  // 🔹 Fetch group info
  useEffect(() => {
    const fetchGroupInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/get_group_info.php?group_id=${numericGroupId}`);
        const data = await res.json();

        if (data.success) {
          setGroupInfo({
            name: data.group.name || "",
            description: data.group.description || "",
            photo: data.group.photo ? `${API_URL}/${data.group.photo}` : "",
            membersCount: data.group.members_count || 0,
            type: data.group.visibility || "",
            createdBy: data.group.created_by ?? null,
            is_active: data.group.is_active,
            scheduled_delete_at: data.group.scheduled_delete_at || null,
            pending_delete: Boolean(data.group.pending_delete),
            is_deactivated: Boolean(data.group.is_deactivated),
          });

          setForm({
            name: data.group.name || "",
            description: data.group.description || "",
            photo: null,
          });
        }
      } catch (err) {
        console.error("Error fetching group info:", err);
      }
    };

    fetchGroupInfo();
  }, [groupId, numericGroupId, API_URL]);

  const applyLifecycleGroup = (group) => {
    if (!group) return;
    setGroupInfo((prev) => ({
      ...prev,
      is_active: group.is_active,
      scheduled_delete_at: group.scheduled_delete_at || null,
      pending_delete: Boolean(group.pending_delete),
      is_deactivated: Boolean(group.is_deactivated),
    }));
    if (onGroupUpdated) onGroupUpdated(group);
  };

  const runLifecycleAction = async (action) => {
    if (!user?.id || !numericGroupId || lifecycleBusy) return;
    setLifecycleBusy(true);
    try {
      const res = await fetch(`${API_URL}/delete_group.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_id: numericGroupId,
          user_id: user.id,
          action: action === "delete" && isAdmin ? "purge" : action,
          confirmed: true,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || "Could not update the group.");
        return;
      }
      if (data.action === "purged") {
        alert(data.message || "Group permanently deleted.");
        navigate(isAdmin ? "/admin/groups" : "/groups/groupinpage");
        return;
      }
      applyLifecycleGroup(data.group);
      setConfirmAction(null);
      alert(data.message);
    } catch (err) {
      console.error("Group lifecycle error:", err);
      alert("Network error while updating the group.");
    } finally {
      setLifecycleBusy(false);
    }
  };

  // 🔸 Update members count
  useEffect(() => {
    if (Array.isArray(members)) {
      setGroupInfo((prev) => ({ ...prev, membersCount: members.length }));
    }
  }, [members]);

  // 📸 Handle photo selection
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, photo: file }));
      setPreview(URL.createObjectURL(file));
    }
  };

  // 💾 Save all updates
  const handleSave = async () => {
    if (!form.name.trim()) return alert("Group name cannot be empty.");
    setSaving(true);

    const formData = new FormData();
    formData.append("group_id", numericGroupId);
    formData.append("name", form.name);
    formData.append("description", form.description);
    if (form.photo) formData.append("photo", form.photo);

    try {
      const res = await fetch(`${API_URL}/update_group_info.php`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        alert("✅ Group info updated successfully!");
        setGroupInfo((prev) => ({
          ...prev,
          name: form.name,
          description: form.description,
          photo: data.new_photo ? `${API_URL}/${data.new_photo}` : prev.photo,
        }));
        setPreview("");
        setForm((prev) => ({ ...prev, photo: null }));
        setIsEditing(false); // <-- close edit mode
      } else {
        alert("❌ " + data.message);
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("Network error while updating group info.");
    } finally {
      setSaving(false);
    }
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const coverSrc = preview || groupInfo.photo || COVER_FALLBACK;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: guestView ? 16 : 20,
        border: "1px solid #e8eef5",
        borderRadius: isMobile && !guestView ? 0 : 16,
        overflow: "hidden",
        backgroundColor: "#fff",
        boxShadow: isMobile && !guestView
          ? "none"
          : "0 2px 14px rgba(15, 23, 42, 0.06)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: guestView || isMobile ? 160 : 200,
          background: "linear-gradient(135deg, #e0f2fe, #f1f5f9 50%, #ccfbf1)",
        }}
      >
        <img
          src={coverSrc}
          alt="Group Cover"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = COVER_FALLBACK;
          }}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        {isEditing && isAdmin && (
          <label
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              background: "rgba(15, 23, 42, 0.72)",
              color: "white",
              padding: "8px 12px",
              borderRadius: 999,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Change Photo
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: "none" }}
            />
          </label>
        )}
      </div>

      <div style={{ padding: guestView ? "18px 20px 20px" : 16 }}>
        {isEditing ? (
          <>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Group Name"
              style={{
                width: "100%",
                padding: "10px 12px",
                marginBottom: 8,
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                fontSize: 16,
                fontWeight: 600,
                boxSizing: "border-box",
              }}
            />

            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Group Description"
              style={{
                width: "100%",
                height: 80,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                resize: "none",
                boxSizing: "border-box",
              }}
            />

            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: saving ? "#94a3b8" : "#10b981",
                color: "white",
                border: "none",
                padding: "9px 16px",
                borderRadius: 10,
                marginTop: 10,
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>

            <button
              onClick={() => setIsEditing(false)}
              style={{
                marginLeft: 10,
                background: "#f1f5f9",
                color: "#334155",
                border: "none",
                padding: "9px 16px",
                borderRadius: 10,
                marginTop: 10,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <h2
              style={{
                margin: 0,
                fontSize: guestView ? 24 : 22,
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "-0.02em",
              }}
            >
              {groupInfo.name}
            </h2>
            <p
              style={{
                margin: "8px 0 0",
                fontSize: 14,
                color: "#64748b",
                lineHeight: 1.5,
              }}
            >
              {groupInfo.description || "No description yet."}
            </p>
            {isAdmin && !guestView && (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  background: "#0284c7",
                  color: "white",
                  border: "none",
                  padding: "8px 14px",
                  borderRadius: 10,
                  cursor: "pointer",
                  marginTop: 12,
                  fontWeight: 600,
                }}
              >
                Update
              </button>
            )}
          </>
        )}

        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#0369a1",
              background: "#e0f2fe",
              padding: "5px 10px",
              borderRadius: 999,
              textTransform: "capitalize",
            }}
          >
            {groupInfo.type || "public"}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#334155",
              background: "#f1f5f9",
              padding: "5px 10px",
              borderRadius: 999,
            }}
          >
            {groupInfo.membersCount}{" "}
            {groupInfo.membersCount === 1 ? "member" : "members"}
          </span>
          {pendingDelete ? (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#9a3412",
                background: "#ffedd5",
                padding: "5px 10px",
                borderRadius: 999,
              }}
            >
              Deletes {formatGroupDeleteAt(groupInfo.scheduled_delete_at) || "in 3 days"}
            </span>
          ) : deactivated ? (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#9f1239",
                background: "#ffe4e6",
                padding: "5px 10px",
                borderRadius: 999,
              }}
            >
              Deactivated
            </span>
          ) : null}
        </div>

        {!guestView && (pendingDelete || deactivated) ? (
          <div
            style={{
              marginTop: 14,
              padding: "12px 14px",
              borderRadius: 12,
              background: pendingDelete ? "#fff7ed" : "#fff1f2",
              border: pendingDelete ? "1px solid #fdba74" : "1px solid #fecdd3",
              color: pendingDelete ? "#9a3412" : "#9f1239",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {pendingDelete
              ? `This group is deactivated and will be permanently deleted on ${
                  formatGroupDeleteAt(groupInfo.scheduled_delete_at) || "the scheduled date"
                }. The owner or a group admin can cancel until then.`
              : "This group is deactivated. Members cannot post or invite until it is restored."}
          </div>
        ) : null}

        {canManage && !guestView ? (
          <div
            style={{
              marginTop: 12,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            {pendingDelete ? (
              <>
                <button
                  type="button"
                  disabled={lifecycleBusy}
                  onClick={() => setConfirmAction("cancel")}
                  style={{
                    background: "#ecfdf5",
                    color: "#047857",
                    border: "1px solid #a7f3d0",
                    padding: "8px 14px",
                    borderRadius: 10,
                    cursor: lifecycleBusy ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  Cancel deletion
                </button>
                {isAdmin ? (
                  <button
                    type="button"
                    disabled={lifecycleBusy}
                    onClick={() => setConfirmAction("delete")}
                    style={{
                      background: "#fef2f2",
                      color: "#b91c1c",
                      border: "1px solid #fecaca",
                      padding: "8px 14px",
                      borderRadius: 10,
                      cursor: lifecycleBusy ? "not-allowed" : "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Delete now
                  </button>
                ) : null}
              </>
            ) : (
              <>
                {deactivated ? (
                  <button
                    type="button"
                    disabled={lifecycleBusy}
                    onClick={() => setConfirmAction("reactivate")}
                    style={{
                      background: "#ecfdf5",
                      color: "#047857",
                      border: "1px solid #a7f3d0",
                      padding: "8px 14px",
                      borderRadius: 10,
                      cursor: lifecycleBusy ? "not-allowed" : "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Reactivate group
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={lifecycleBusy}
                    onClick={() => setConfirmAction("deactivate")}
                    style={{
                      background: "#fff7ed",
                      color: "#c2410c",
                      border: "1px solid #fdba74",
                      padding: "8px 14px",
                      borderRadius: 10,
                      cursor: lifecycleBusy ? "not-allowed" : "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Deactivate group
                  </button>
                )}
                <button
                  type="button"
                  disabled={lifecycleBusy}
                  onClick={() => setConfirmAction("delete")}
                  style={{
                    background: "#fef2f2",
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    padding: "8px 14px",
                    borderRadius: 10,
                    cursor: lifecycleBusy ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  Delete group
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>

      {confirmAction ? (
        <div
          role="presentation"
          onClick={() => !lifecycleBusy && setConfirmAction(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            zIndex: 12000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="group-lifecycle-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              background: "#fff",
              borderRadius: 16,
              padding: 22,
              boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
            }}
          >
            <h3
              id="group-lifecycle-title"
              style={{ margin: "0 0 8px", fontSize: 18, color: "#0f172a" }}
            >
              {confirmAction === "delete"
                ? isAdmin
                  ? "Permanently delete this group?"
                  : "Delete this group?"
                : confirmAction === "deactivate"
                ? "Deactivate this group?"
                : confirmAction === "reactivate"
                ? "Reactivate this group?"
                : "Cancel scheduled deletion?"}
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: "#475569", lineHeight: 1.55 }}>
              {confirmAction === "delete"
                ? isAdmin
                  ? "This group will be permanently deleted now, even if someone else owns it. This cannot be undone."
                  : "The group will be deactivated now and permanently deleted after 3 days. You can cancel this from the group page until then."
                : confirmAction === "deactivate"
                ? "Members will no longer be able to post or invite. You can reactivate the group later."
                : confirmAction === "reactivate"
                ? "The group will become active again. Members will be able to post and invite."
                : "The 3-day deletion will be cancelled and the group will become active again."}
            </p>
            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 18,
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                disabled={lifecycleBusy}
                onClick={() => setConfirmAction(null)}
                style={{
                  background: "#f1f5f9",
                  color: "#334155",
                  border: "none",
                  padding: "9px 14px",
                  borderRadius: 10,
                  cursor: lifecycleBusy ? "not-allowed" : "pointer",
                  fontWeight: 600,
                }}
              >
                Go back
              </button>
              <button
                type="button"
                disabled={lifecycleBusy}
                onClick={() => runLifecycleAction(confirmAction)}
                style={{
                  background:
                    confirmAction === "delete" || confirmAction === "deactivate"
                      ? "#dc2626"
                      : "#047857",
                  color: "#fff",
                  border: "none",
                  padding: "9px 14px",
                  borderRadius: 10,
                  cursor: lifecycleBusy ? "not-allowed" : "pointer",
                  fontWeight: 600,
                }}
              >
                {lifecycleBusy
                  ? "Please wait..."
                  : confirmAction === "delete"
                  ? isAdmin
                    ? "Yes, delete permanently"
                    : "Yes, delete in 3 days"
                  : confirmAction === "deactivate"
                  ? "Yes, deactivate"
                  : confirmAction === "reactivate"
                  ? "Yes, reactivate"
                  : "Yes, cancel deletion"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
